
import React, { useState, useEffect } from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Coins } from 'lucide-react';
import { PointsService } from '@/services/points/PointsService';
import { toast } from 'sonner';

interface PollPointsToggleProps {
  pollId: string;
  pollTitle: string;
  disabled?: boolean;
}

const PollPointsToggle: React.FC<PollPointsToggleProps> = ({ 
  pollId, 
  pollTitle, 
  disabled = false 
}) => {
  const [pointsEnabled, setPointsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadPointsStatus();
  }, [pollId]);

  const loadPointsStatus = async () => {
    try {
      const enabled = await PointsService.isPollPointsEnabled(pollId);
      setPointsEnabled(enabled);
    } catch (error) {
      console.error('Error loading poll points status:', error);
    }
  };

  const handleToggle = async (enabled: boolean) => {
    setIsLoading(true);
    try {
      const result = await PointsService.togglePollPoints(pollId, enabled);
      
      if (result.success) {
        setPointsEnabled(enabled);
        toast.success(`Points ${enabled ? 'enabled' : 'disabled'} for poll: ${pollTitle}`);
      } else {
        toast.error(result.error || 'Failed to update poll points setting');
      }
    } catch (error) {
      console.error('Error toggling poll points:', error);
      toast.error('Failed to update poll points setting');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
      <Coins className="h-4 w-4 text-yellow-500" />
      <div className="flex-1">
        <Label htmlFor={`points-${pollId}`} className="text-sm font-medium">
          Points Rewards
        </Label>
        <p className="text-xs text-gray-600">Allow users to earn points for voting</p>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={pointsEnabled ? "default" : "secondary"} className="text-xs">
          {pointsEnabled ? "Enabled" : "Disabled"}
        </Badge>
        <Switch
          id={`points-${pollId}`}
          checked={pointsEnabled}
          onCheckedChange={handleToggle}
          disabled={disabled || isLoading}
        />
      </div>
    </div>
  );
};

export default PollPointsToggle;
